import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Package, RefreshCw, Trash2, Heart } from 'lucide-react';
import { useAuthStore } from '@/store';
import { apiClient } from '@/lib/api-client';
import { formatDate } from '@/utils';

// ─── Wishlist message helpers ──────────────────────────────────

interface ParsedWishlistMessage {
  type: 'buyer' | 'seller';
  imageUrl: string | null;
  text: string;
}

/**
 * If the raw message content begins with [WISHLIST_MATCH:buyer] or
 * [WISHLIST_MATCH:seller], parse it into structured form.
 * Returns null for regular messages.
 */
function parseWishlistMessage(content: string): ParsedWishlistMessage | null {
  const buyerPrefix  = '[WISHLIST_MATCH:buyer]';
  const sellerPrefix = '[WISHLIST_MATCH:seller]';

  let type: 'buyer' | 'seller';
  let rest: string;

  if (content.startsWith(buyerPrefix)) {
    type = 'buyer';
    rest = content.slice(buyerPrefix.length);
  } else if (content.startsWith(sellerPrefix)) {
    type = 'seller';
    rest = content.slice(sellerPrefix.length);
  } else {
    return null;
  }

  // Strip leading newline
  if (rest.startsWith('\n')) rest = rest.slice(1);

  // Extract optional [CARD_IMAGE:url] line
  let imageUrl: string | null = null;
  const imgMatch = rest.match(/^\[CARD_IMAGE:([^\]]*)\]\n?/);
  if (imgMatch) {
    imageUrl = imgMatch[1] || null;
    rest = rest.slice(imgMatch[0].length);
  }

  return { type, imageUrl, text: rest.trim() };
}

/**
 * Replace bare /listings/<uuid> paths in a string with clickable React Router Links.
 * Returns an array of strings and JSX elements.
 */
function linkifyListingPaths(text: string): React.ReactNode[] {
  const parts = text.split(/(\/listings\/[0-9a-f-]{36})/gi);
  return parts.map((part, i) => {
    if (/^\/listings\/[0-9a-f-]{36}$/i.test(part)) {
      return (
        <Link key={i} to={part} className="underline font-medium hover:text-blue-800 dark:text-blue-200">
          View listing →
        </Link>
      );
    }
    return part;
  });
}

interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  sender_username: string;
  sender_avatar?: string;
  content: string;
  is_read: boolean;
  created_at: string;
}

interface Conversation {
  id: string;
  other_user_id: string;
  other_username: string;
  other_avatar?: string;
  listing_title?: string;
  listing_id?: string;
}

export default function ConversationPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hoveredMsg, setHoveredMsg] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = (smooth = true) => {
    messagesEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
  };

  const fetchMessages = useCallback(async (silent = false) => {
    if (!id) return;
    if (!silent) setIsLoading(true);
    else setIsRefreshing(true);
    try {
      const [convRes, msgRes] = await Promise.all([
        apiClient.get<any>('/conversations'),
        apiClient.get<any>(`/conversations/${id}/messages`),
      ]);
      const convs = convRes.data ?? [];
      setConversation(convs.find((c: any) => c.id === id) ?? null);
      const msgs: Message[] = msgRes.data ?? [];
      setMessages(msgs);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [id]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  useEffect(() => {
    const interval = setInterval(() => fetchMessages(true), 30000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  useEffect(() => { if (!isLoading) scrollToBottom(false); }, [isLoading]);
  const prevMsgCount = useRef(0);
  const isDeleting = useRef(false);
  const isSendingRef = useRef(false);
  useEffect(() => {
    if (messages.length > prevMsgCount.current) {
      scrollToBottom();
    }
    prevMsgCount.current = messages.length;
  }, [messages.length]);

  const handleSend = useCallback(async () => {
    if (!input.trim() || !id || isSending) return;
    const content = input.trim();
    setInput('');
    setIsSending(true);

    isSendingRef.current = true;
    const tempMsg: Message = {
      id: `temp-${Date.now()}`,
      conversation_id: id,
      sender_id: user?.id ?? '',
      sender_username: user?.username ?? '',
      content,
      is_read: false,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempMsg]);

    try {
      const response = await apiClient.post<any>(`/conversations/${id}/messages`, { content });
      const newMsg = response.data;
      setMessages((prev) => prev.map((m) => m.id === tempMsg.id ? newMsg : m));
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages((prev) => prev.filter((m) => m.id !== tempMsg.id));
      setInput(content);
    } finally {
      setIsSending(false);
      textareaRef.current?.focus();
    }
  }, [input, id, isSending, user]);

  const handleDeleteMessage = async (messageId: string) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await apiClient.delete(`/conversations/${id}/messages/${messageId}`);
      isDeleting.current = true;
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
    } catch (error) {
      console.error('Failed to delete message:', error);
    }
  };

  const handleDeleteConversation = async () => {
    if (!window.confirm('Delete this entire conversation? This cannot be undone.')) return;
    try {
      await apiClient.delete(`/conversations/${id}`);
      navigate('/messages');
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-[calc(100vh-64px)]">
        <div className="bg-white dark:bg-zinc-900 border-b px-4 py-3">
          <div className="skeleton h-8 w-48 rounded" />
        </div>
        <div className="flex-1 p-4 space-y-3">
          {[...Array(5)].map((_, i) => <div key={i} className={`skeleton h-12 rounded-2xl ${i % 2 === 0 ? 'w-2/3' : 'w-1/2 ml-auto'}`} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] bg-white dark:bg-zinc-900 overflow-hidden">
      {/* Header */}
      <div className="bg-white dark:bg-zinc-900 border-b px-4 py-3 flex items-center gap-3 shadow-sm">
        <Link to="/messages" className="text-gray-500 dark:text-zinc-500 hover:text-gray-800 dark:text-zinc-200 transition-colors">
          <ArrowLeft className="h-5 w-5" />
        </Link>

        <div className="h-9 w-9 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-600 dark:text-brand-400 flex items-center justify-center font-bold flex-shrink-0">
          {conversation?.other_avatar ? (
            <img src={conversation.other_avatar} alt={conversation.other_username} className="h-full w-full rounded-full object-cover" />
          ) : (
            conversation?.other_username?.[0]?.toUpperCase()
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">{conversation?.other_username ?? 'Unknown User'}</p>
          {conversation?.listing_title && (
            <p className="text-xs text-brand-600 dark:text-brand-400 flex items-center gap-1 truncate">
              <Package className="h-3 w-3" />
              {conversation.listing_title}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {conversation?.listing_id && (
            <Link to={`/listings/${conversation.listing_id}`} className="text-xs text-brand-600 dark:text-brand-400 hover:underline font-medium">
              View Listing →
            </Link>
          )}
          <button onClick={() => fetchMessages(true)} disabled={isRefreshing} className="text-gray-500 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-400 dark:text-zinc-400 p-1">
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={handleDeleteConversation} className="text-gray-500 dark:text-zinc-500 hover:text-red-500 p-1 transition-colors" title="Delete conversation">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2">
        {messages.length === 0 && (
          <div className="text-center py-12 text-gray-500 dark:text-zinc-500 text-sm">No messages yet. Say hello!</div>
        )}

        {messages.map((msg, index) => {
          const isOwn = msg.sender_id === user?.id;
          const prevMsg = messages[index - 1];
          const nextMsg = messages[index + 1];
          const showName = !isOwn && (!prevMsg || prevMsg.sender_id !== msg.sender_id);
          const isLastInGroup = !nextMsg || nextMsg.sender_id !== msg.sender_id;
          const showTime = isLastInGroup && (!nextMsg || new Date(nextMsg.created_at).getTime() - new Date(msg.created_at).getTime() > 5 * 60 * 1000);

          const wishlist = parseWishlistMessage(msg.content);

          // ── Wishlist match bubble ──────────────────────────────
          if (wishlist) {
            const isBuyerMsg  = wishlist.type === 'buyer';
            const borderColor = isBuyerMsg ? 'border-l-blue-400' : 'border-l-green-500';
            const bgColor     = isBuyerMsg ? 'bg-blue-50 dark:bg-blue-900/20'        : 'bg-green-50 dark:bg-green-900/20';
            const labelColor  = isBuyerMsg ? 'text-blue-600'     : 'text-green-700 dark:text-green-300';
            const labelBg     = isBuyerMsg ? 'bg-blue-100 dark:bg-blue-900/40'       : 'bg-green-100 dark:bg-green-900/40';

            return (
              <div key={msg.id} className="flex justify-center my-2">
                <div
                  className={`w-full max-w-lg rounded-xl border border-l-4 ${borderColor} ${bgColor} border-gray-200 dark:border-zinc-800 shadow-sm overflow-hidden ${
                    msg.id.startsWith('temp-') ? 'opacity-70' : ''
                  }`}
                >
                  {/* Header row */}
                  <div className={`flex items-center gap-2 px-4 pt-3 pb-1`}>
                    <Heart className={`h-3.5 w-3.5 ${labelColor}`} />
                    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${labelBg} ${labelColor}`}>
                      Wishlist match
                    </span>
                  </div>

                  <div className="flex gap-3 px-4 py-3">
                    {/* Card thumbnail */}
                    {wishlist.imageUrl && (
                      <div className="flex-shrink-0 w-12 h-16 rounded-md overflow-hidden bg-gray-200 dark:bg-zinc-700 shadow-sm">
                        <img
                          src={wishlist.imageUrl}
                          alt="card"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}

                    {/* Message text with linkified listing path */}
                    <p className="text-sm text-gray-800 dark:text-zinc-200 leading-relaxed">
                      {linkifyListingPaths(wishlist.text)}
                    </p>
                  </div>

                  {showTime && (
                    <div className="px-4 pb-2 text-xs text-gray-500 dark:text-zinc-500">
                      {formatDate(msg.created_at, 'MMM d, h:mm a')}
                    </div>
                  )}
                </div>
              </div>
            );
          }

          // ── Regular message bubble ─────────────────────────────
          return (
            <div
              key={msg.id}
              className={`flex items-end gap-2 group ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}
              onMouseEnter={() => setHoveredMsg(msg.id)}
              onMouseLeave={() => setHoveredMsg(null)}
            >
              {!isOwn && (
                <div className={`h-7 w-7 rounded-full flex-shrink-0 ${isLastInGroup ? 'bg-brand-100 dark:bg-brand-900/40 text-brand-600 dark:text-brand-400 flex items-center justify-center text-xs font-bold' : 'invisible'}`}>
                  {isLastInGroup && msg.sender_username?.[0]?.toUpperCase()}
                </div>
              )}

              <div className={`flex items-end gap-1.5 max-w-xs lg:max-w-md ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}>
                <div className={`flex flex-col gap-0.5 ${isOwn ? 'items-end' : 'items-start'}`}>
                  {showName && <p className="text-xs text-gray-500 dark:text-zinc-500 px-1 mb-0.5">{msg.sender_username}</p>}
                  <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    msg.id.startsWith('temp-') ? 'opacity-70' : ''
                  } ${
                    isOwn
                      ? 'bg-brand-600 text-white rounded-br-sm'
                      : 'bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 text-gray-800 dark:text-zinc-200 rounded-bl-sm shadow-sm'
                  }`}>
                    {msg.content}
                  </div>
                  {showTime && (
                    <span className="text-xs text-gray-500 dark:text-zinc-500 px-1">
                      {formatDate(msg.created_at, 'MMM d, h:mm a')}
                    </span>
                  )}
                </div>

                {/* Delete button - only own messages */}
                {isOwn && hoveredMsg === msg.id && !msg.id.startsWith('temp-') && (
                  <button
                    onClick={() => handleDeleteMessage(msg.id)}
                    className="text-gray-400 dark:text-zinc-500 hover:text-red-400 transition-colors flex-shrink-0 mb-1"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          );
        })}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="bg-white dark:bg-zinc-900 border-t px-4 py-3">
        <div className="flex items-end gap-3 max-w-3xl mx-auto">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message... (Enter to send, Shift+Enter for new line)"
            rows={1}
            className="flex-1 resize-none border border-gray-200 dark:border-zinc-800 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 max-h-32"
            style={{ minHeight: '42px' }}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isSending}
            className="h-10 w-10 bg-brand-600 hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl flex items-center justify-center transition-colors flex-shrink-0"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}